import Phaser from 'phaser';
import { BuildingData, BuildingStateData, socketManager } from '../socket/SocketManager';
import { BaseInterior } from '../interiors/BaseInterior';
import { DealPointInterior } from '../interiors/DealPointInterior';
import { BusinessInterior } from '../interiors/BusinessInterior';
import { WholesalerInterior } from '../interiors/WholesalerInterior';
import { PoliceInterior } from '../interiors/PoliceInterior';
import { HouseChoiceInterior } from '../interiors/HouseChoiceInterior';
import { StashInterior } from '../interiors/StashInterior';
import { GrowRoomInterior } from '../interiors/GrowRoomInterior';
import { CasinoInterior } from '../interiors/CasinoInterior';

export class InteriorScene extends Phaser.Scene {
  private interior: BaseInterior | null = null;
  private building: BuildingData | null = null;
  private stateData: BuildingStateData | null = null;

  constructor() {
    super({ key: 'InteriorScene' });
  }

  init(data: { building: BuildingData; stateData: BuildingStateData | null }): void {
    this.building = data.building;
    this.stateData = data.stateData;
  }

  create(): void {
    if (!this.building) return;

    console.log(`[INTERIOR] Creating interior for building: ${this.building.name} (type=${this.building.type}, id=${this.building.id})`);

    // Fade in
    this.cameras.main.fadeIn(300, 0, 0, 0);

    // Create the appropriate interior based on building type
    this.interior = this.createInterior(this.building);

    if (this.interior) {
      this.interior.render();
      this.interior.bindEvents();
      this.interior.onExit(() => this.exitToCity());
    }

    // Listen for updated building state
    socketManager.onActionResult((result) => {
      if (result.data && this.building) {
        // Rebuild interior with new data
        const newState = result.data as unknown as BuildingStateData;
        if (newState.building) {
          this.building = newState.building;
          this.stateData = newState;
          this.rebuildInterior();
        }
      }
    });

    // Keyboard exit
    this.input.keyboard!.on('keydown-ESC', () => this.exitToCity());
  }

  private createInterior(building: BuildingData): BaseInterior | null {
    switch (building.type) {
      case 'DEAL_POINT':
        return new DealPointInterior(this, building, this.stateData);
      case 'HOUSE':
      case 'APARTMENT':
        if (building.assignedRole === 'STASH' && this.stateData?.stashInfo) {
          return new StashInterior(this, building, this.stateData);
        }
        if (building.assignedRole === 'DEAL_POINT' && this.stateData?.dealPointInfo) {
          return new DealPointInterior(this, building, this.stateData);
        }
        return new HouseChoiceInterior(this, building, this.stateData);
      case 'SHOP':
        return new BusinessInterior(this, building, this.stateData);
      case 'WHOLESALER':
        return new WholesalerInterior(this, building, this.stateData);
      case 'WAREHOUSE':
        return new GrowRoomInterior(this, building, this.stateData);
      case 'POLICE_STATION':
        return new PoliceInterior(this, building, this.stateData);
      case 'CASINO':
        return new CasinoInterior(this, building, this.stateData);
      default:
        // Unknown type — still show a basic interior instead of nothing
        return new DealPointInterior(this, building, this.stateData);
    }
  }

  private rebuildInterior(): void {
    if (!this.building) return;
    console.log(`[INTERIOR] Rebuilding interior for: ${this.building.name} (type=${this.building.type})`);
    this.interior?.destroy();
    this.interior = null;
    // Remove any leftover game objects so the old view is fully cleared
    this.children.removeAll(true);
    this.interior = this.createInterior(this.building);
    if (this.interior) {
      this.interior.render();
      this.interior.bindEvents();
      this.interior.onExit(() => this.exitToCity());
    }
  }

  private exitToCity(): void {
    console.log(`[INTERIOR] Exiting to city from: ${this.building?.name}`);
    this.cameras.main.fadeOut(200, 0, 0, 0);
    this.time.delayedCall(200, () => {
      this.interior?.destroy();
      this.interior = null;
      this.scene.stop('InteriorScene');
      this.scene.resume('GameScene');
    });
  }
}
